import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface NomineeInfo {
  type: 'teacher' | 'prospect';
  name: string | null;
  school?: string | null;
  weight: number;
  entity_id: string;
}

export default function Support() {
  const { code } = useParams<{ code: string }>();

  const [nominee, setNominee] = useState<NomineeInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [supporterName, setSupporterName] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newWeight, setNewWeight] = useState<number | null>(null);

  // Load nominee info
  useEffect(() => {
    if (!code) return;
    fetch(`${CORE_API}/api/v1/nominations/support/${code}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.error) throw new Error(json.error.message || 'Not found');
        setNominee(json.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [code]);

  const handleSupport = async () => {
    if (!code) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${CORE_API}/api/v1/nominations/support/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supporter_name: supporterName.trim() || null,
          note: note.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message || 'Failed');
      setNewWeight(json.data.new_weight);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !nominee) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 px-4">
        <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-slate-800">Support Link</h1>
          <p className="mt-2 text-sm text-red-500">{error || 'Nominee not found'}</p>
          <a href="/" className="mt-4 inline-block text-sm text-amber-600 hover:underline">Back to Project Tower</a>
        </div>
      </div>
    );
  }

  const displayWeight = newWeight ?? nominee.weight;
  const kindLabel = nominee.type === 'teacher' ? 'Teacher' : 'Prospect';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            Support {nominee.name || 'This Nominee'}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {kindLabel} Nominee{nominee.school ? ` at ${nominee.school}` : ''}
          </p>
        </div>

        {/* Support count */}
        <div className="my-6 rounded-xl bg-amber-50 p-4 text-center">
          <p className="text-3xl font-bold text-amber-700">{displayWeight}</p>
          <p className="text-xs text-amber-600 mt-1">
            {displayWeight === 1 ? 'supporter' : 'supporters'}
          </p>
        </div>

        {submitted ? (
          <div className="text-center space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-slate-800">Thank you for your support!</p>
            <p className="text-sm text-slate-500">Your vote has been counted.</p>
            <button
              onClick={() => { setSubmitted(false); setSupporterName(''); setNote(''); }}
              className="mt-2 text-sm text-amber-600 hover:underline"
            >
              Support again
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Your first name (optional)</label>
              <input
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={supporterName}
                onChange={(e) => setSupporterName(e.target.value)}
                placeholder="First name only"
                maxLength={50}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Leave a note (optional)</label>
              <textarea
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A quick message of support..."
                maxLength={280}
              />
            </div>
            <button
              onClick={handleSupport}
              disabled={submitting}
              className="w-full rounded-xl bg-amber-500 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:opacity-50"
            >
              {submitting ? 'Adding support...' : 'Support This Nominee'}
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-[10px] text-slate-400">
          Project Tower — Building communities, one classroom at a time.
        </p>
      </div>
    </div>
  );
}
